import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { finishBooking } from "../api/api";
import loader from "../assets/images/loader.gif";
import { resetBooking } from "../features/bookingSlice/bookingSlice";
import { FaFilePdf, FaPrint, FaCheckCircle, FaHome, FaCalendarAlt, FaCar, FaCreditCard, FaPhone, FaEnvelope } from "react-icons/fa";

const ThankYouPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const apiCalled = useRef(false); // Prevent duplicate API calls

  // Get booking data from Redux store
  const { booking_id, quote_info_record_id, selected_vehicle_id, card } = useSelector(
    (state) => state.booking
  );

  // Check for Square payment data from location state
  const squarePaymentData = location.state;

  useEffect(() => {
    const completeBooking = async () => {
      // Handle Square payment completion from navigation state
      if (squarePaymentData?.bookingRefId) {
        console.log('Square payment data received:', squarePaymentData);
        setBookingId(squarePaymentData.bookingRefId);
        setPaymentData({
          transaction_id: squarePaymentData.transactionId,
          amount: squarePaymentData.amount,
          payment_method: squarePaymentData.paymentMethod
        });
        setMessage("Your booking has been confirmed and payment processed successfully!");
        setLoading(false);
        dispatch(resetBooking());
        return;
      }

      // Handle case where we have booking_id but no Square payment data
      if (booking_id) {
        console.log('Booking ID found, completing booking:', booking_id);
        setBookingId(booking_id);
        setMessage("Your booking has been confirmed successfully!");
        setLoading(false);
        dispatch(resetBooking());
        return;
      }

      // No booking data found
      console.log('No booking data found, redirecting...');
      setMessage("No booking information found.");
      setLoading(false);
      setTimeout(() => {
        navigate('/');
      }, 3000);
    };

    completeBooking();
  }, [booking_id, dispatch, squarePaymentData, navigate]);

  // Helper to get parent domain
  function getParentDomainUrl() {
    const { hostname, protocol } = window.location;
    const parts = hostname.split('.');
    if (parts.length >= 3) {
      return `${protocol}//${parts.slice(1).join('.')}`;
    }
    return `${protocol}//${hostname}`;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <img src={loader} alt="Loading..." width="200px" />
      </div>
    );
  }

  return (
    <>
      <style>{`
        .btn-main {
          background: #188038;
          border: 0;
          border-radius: 0;
          color: #fff;
          display: block;
          font-size: 16px;
          font-weight: 800;
          letter-spacing: 0.2px;
          padding: 15px 30px;
          text-align: center;
          text-decoration: none;
          text-transform: uppercase;
          width: 300px;
          margin: auto;
        }
        
        .thankyou-success-icon {
          width: 80px;
          height: 80px;
          background: #188038;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 30px;
        }
        
        .thankyou-title {
          color: #188038;
          font-size: 28px;
          font-weight: 900;
          text-transform: uppercase;
          margin-bottom: 20px;
          font-family: "Sora", serif;
        }
        
        .thankyou-subtitle {
          font-size: 16px;
          color: #6a6a6a;
          margin-bottom: 30px;
          line-height: 1.5;
          font-family: "Sora", serif;
        }
        
        .info-section {
          margin-bottom: 30px;
        }
        
        .info-card {
          background: #f8f8f8;
          border: 2px solid #e9e9e9;
          border-radius: 0;
          padding: 20px;
          margin-bottom: 20px;
          text-align: left;
        }
        
        .info-card-header {
          display: flex;
          align-items: center;
          margin-bottom: 15px;
        }
        
        .info-card-icon {
          width: 40px;
          height: 40px;
          background: #188038;
          color: white;
          border-radius: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 15px;
          font-size: 18px;
        }
        
        .info-card-title {
          font-size: 18px;
          font-weight: 800;
          color: #188038;
          margin: 0;
          font-family: "Sora", serif;
          text-transform: uppercase;
        }
        
        .booking-reference {
          font-size: 24px;
          font-weight: 800;
          color: #188038;
          font-family: "Sora", serif;
          letter-spacing: 1px;
          margin-bottom: 10px;
        }
        
        .reference-note {
          font-size: 14px;
          color: #6a6a6a;
          font-weight: 600;
          margin: 0;
        }
        
        .payment-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          margin-top: 15px;
        }
        
        .payment-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 15px;
          background: white;
          border: 1px solid #e9e9e9;
          border-radius: 0;
        }
        
        .payment-label {
          font-size: 14px;
          color: #6a6a6a;
          font-weight: 600;
        }
        
        .payment-value {
          font-size: 14px;
          color: #188038;
          font-weight: 800;
        }
        
        .demo-notice {
          background: #d5ffc8;
          border: 2px solid #188038;
          border-radius: 0;
          padding: 20px;
          margin: 20px 0;
          text-align: center;
        }
        
        .demo-notice-title {
          font-weight: 800;
          color: #188038;
          margin-bottom: 10px;
          font-size: 16px;
          text-transform: uppercase;
          font-family: "Sora", serif;
        }
        
        .demo-notice-text {
          color: #188038;
          margin: 0;
          font-size: 14px;
          font-weight: 600;
        }
        
        .actions-section {
          border-top: 2px solid #e9e9e9;
          padding-top: 30px;
          margin-top: 30px;
        }
        
        .pdf-links {
          margin-bottom: 30px;
          text-align: center;
        }
        
        .pdf-link {
          display: inline-flex;
          align-items: center;
          color: #188038;
          text-decoration: none;
          font-weight: 700;
          font-size: 16px;
          margin: 0 15px;
          font-family: "Sora", serif;
        }
        
        .pdf-link:hover {
          color: #229556;
          text-decoration: underline;
        }
        
        .pdf-link svg {
          margin-right: 8px;
        }
        
        @media (max-width: 768px) {
          .payment-grid {
            grid-template-columns: 1fr;
          }
          
          .pdf-link {
            display: block;
            margin: 10px 0;
          }
          
          .thankyou-title {
            font-size: 24px;
          }
        }
      `}</style>
      
      <div className="container-fluid dim-bg thankyou-page">
        <div className="floating-sidebar text-center p-[100px] whitespace-normal" style={{ minHeight: "unset", maxHeight: "none"}}>
          
          <div className="thankyou-success-icon">
            <FaCheckCircle className="text-white text-3xl" />
          </div>
          
          <h1 className="thankyou-title">Thank You!</h1>
          <p className="thankyou-subtitle">
            {message || "Thank you for choosing our service. Your booking has been successfully processed."}
          </p>
          
          <div className="info-section">
            {/* Booking Reference Card */}
            {bookingId && (
              <div className="info-card">
                <div className="info-card-header">
                  <div className="info-card-icon">
                    <FaCar />
                  </div>
                  <h3 className="info-card-title">Booking Reference</h3>
                </div>
                <div className="booking-reference">{bookingId}</div>
                <p className="reference-note">
                  Keep this reference number for your records
                </p>
              </div>
            )}
            
            {/* Payment Details Card */}
            {paymentData && (
              <div className="info-card">
                <div className="info-card-header">
                  <div className="info-card-icon">
                    <FaCreditCard />
                  </div>
                  <h3 className="info-card-title">Payment Confirmation</h3>
                </div>
                <div className="payment-grid">
                  <div className="payment-item">
                    <span className="payment-label">Amount</span>
                    <span className="payment-value">${paymentData.amount || 'N/A'}</span>
                  </div>
                  {/* <div className="payment-item">
                    <span className="payment-label">Method</span>
                    <span className="payment-value">Square</span>
                  </div> */}
                  <div className="payment-item">
                    <span className="payment-label">Status</span>
                    <span className="payment-value">✓ Successful</span>
                  </div>
                  {/* <div className="payment-item">
                    <span className="payment-label">Transaction ID</span>
                    <span className="payment-value">{paymentData.transaction_id?.slice(-8) || 'N/A'}</span>
                  </div> */}
                </div>
              </div>
            )}
          </div>
          
          {/* Demo Mode Notice */}
          {squarePaymentData?.demoMode && (
            <div className="demo-notice">
              <h4 className="demo-notice-title">Demo Mode Active</h4>
              <p className="demo-notice-text">
                This payment was processed in demo mode. Real payment processing requires backend configuration.
              </p>
            </div>
          )}
          
          <div className="actions-section">
            {/* PDF Download Links */}
            {bookingId && (
              <div className="pdf-links">
                <a
                  className="pdf-link"
                  href={`https://dispatch.gologanairport.com/api/quote/download-pdf?booking_ref_id=${bookingId}`}
                >
                  <FaFilePdf />
                  Download PDF
                </a>
                <a
                  className="pdf-link"
                  href={`https://dispatch.gologanairport.com/api/quote/download-pdf?booking_ref_id=${bookingId}&print=1`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaPrint />
                  Print Invoice
                </a>
              </div>
            )}
            
            {/* Home Button */}
            <button
              className="btn-main"
              onClick={() => { window.location.href = getParentDomainUrl(); }}
            >
              Back to Homepage
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ThankYouPage;
